import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Download, RefreshCw, Image, X, Filter, ChevronDown, ChevronRight, CheckCircle, Circle, Edit } from 'lucide-react';

const AdminReports = () => {
  const [activeTab, setActiveTab] = useState('zone-attendance');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [imageModal, setImageModal] = useState(null);
  const [verifyingRecord, setVerifyingRecord] = useState(null); // Track which record is being verified
  
  // Filter states
  const [zones, setZones] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Collapsible zones state
  const [collapsedZones, setCollapsedZones] = useState({});
  
  // Verification filter for zone attendance (All, Verified, Pending)
  const [verificationFilter, setVerificationFilter] = useState('pending');
  
  // Employee Master states
  const [employeeMasterData, setEmployeeMasterData] = useState(null);
  const [employeeMasterSummary, setEmployeeMasterSummary] = useState(null); // Always stores full summary
  const [employeeCategoryFilter, setEmployeeCategoryFilter] = useState('all');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchZones();
  }, [user, userType, navigate, authLoading]);

  // Fetch data when tab or date changes - but only on initial load or explicit change
  useEffect(() => {
    if (!user || userType !== 'admin' || authLoading) return;
    fetchData();
  }, [activeTab, date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch employee master when zone changes (not category - that's client-side filtering)
  useEffect(() => {
    if (activeTab === 'employee-master' && user && userType === 'admin' && selectedZone !== undefined) {
      setEmployeeMasterSummary(null); // Reset summary to refetch
      fetchData();
    }
  }, [selectedZone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch zones for filter
  const fetchZones = async () => {
    try {
      const response = await adminAPI.getZones();
      setZones(sortNumeric(response.data.zones || []));
    } catch (error) {
      console.error('Failed to fetch zones');
    }
  };

  // Fetch divisions when zone changes
  useEffect(() => {
    if (selectedZone) {
      fetchDivisions(selectedZone);
    } else {
      setDivisions([]);
      setSelectedDivision('');
    }
  }, [selectedZone]);

  const fetchDivisions = async (zone) => {
    try {
      const response = await adminAPI.getDivisions(zone);
      setDivisions(sortNumeric(response.data.divisions || []));
    } catch (error) {
      console.error('Failed to fetch divisions');
    }
  };

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      let response;
      if (activeTab === 'zone-attendance') {
        response = await adminAPI.getZoneAttendance(date);
        setData(response.data);
      } else if (activeTab === 'closed-parks') {
        response = await adminAPI.getClosedParks(date);
        setData(response.data);
      } else if (activeTab === 'no-manpower') {
        response = await adminAPI.getNoManpowerParks(date);
        setData(response.data);
      } else if (activeTab === 'temp-workers') {
        response = await adminAPI.getTempWorkerReport(date);
        setData(response.data);
      } else if (activeTab === 'employee-master') {
        // Always fetch full data without category filter
        response = await adminAPI.getEmployeeMaster(selectedZone || 'all', 'all');
        setEmployeeMasterData(response.data);
        // Store full summary for dashboard (only update if not set or zone changed)
        if (!employeeMasterSummary || isManualRefresh) {
          setEmployeeMasterSummary(response.data.summary);
        }
      }
      if (isManualRefresh) {
        toast.success('Report refreshed');
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedZone('');
    setSelectedDivision('');
  };

  // Zone collapse/expand functions
  const toggleZone = (zone) => {
    setCollapsedZones(prev => ({
      ...prev,
      [zone]: !prev[zone]
    }));
  };

  const collapseAllZones = () => {
    const zones = Object.keys(getFilteredZonesData());
    const allCollapsed = {};
    zones.forEach(z => allCollapsed[z] = true);
    setCollapsedZones(allCollapsed);
  };

  const expandAllZones = () => setCollapsedZones({});

  // Get unique parks from zone data (for counting)
  const getUniqueParksByZone = (zoneData) => {
    if (!zoneData) return {};
    const parkMap = {};
    zoneData.forEach(emp => {
      if (!parkMap[emp.park_id]) {
        parkMap[emp.park_id] = {
          park_id: emp.park_id,
          park_name: emp.park_name,
          verified: emp.verified === true,
          workers: []
        };
      }
      parkMap[emp.park_id].workers.push(emp);
      // Park is verified if ANY worker record for that park is verified
      if (emp.verified === true) {
        parkMap[emp.park_id].verified = true;
      }
    });
    return parkMap;
  };

  // Calculate verification counts based on unique PARKS (not records)
  const getVerificationCounts = () => {
    if (!data?.zones) return { total: 0, verified: 0, pending: 0 };
    const seenParks = new Set();
    let verified = 0, pending = 0;
    
    Object.values(data.zones).forEach(employees => {
      const parkMap = getUniqueParksByZone(employees);
      Object.values(parkMap).forEach(park => {
        if (!seenParks.has(park.park_id)) {
          seenParks.add(park.park_id);
          if (park.verified) {
            verified++;
          } else {
            pending++;
          }
        }
      });
    });
    return { total: seenParks.size, verified, pending };
  };

  // Get park counts for a specific zone (from ORIGINAL data, not filtered)
  const getZoneParkCounts = (zoneName) => {
    if (!data?.zones || !data.zones[zoneName]) return { total: 0, verified: 0, pending: 0 };
    const zoneEmployees = data.zones[zoneName];
    const parkMap = getUniqueParksByZone(zoneEmployees);
    const parks = Object.values(parkMap);
    const verified = parks.filter(p => p.verified).length;
    const pending = parks.filter(p => !p.verified).length;
    return { total: parks.length, verified, pending };
  };

  // Handle verifying a PARK (marks all workers in that park as verified)
  const handleVerifyPark = async (parkId, parkName) => {
    setVerifyingRecord(parkId);
    try {
      await adminAPI.verifyZoneAttendancePark(date, parkId, true);
      
      // Update local state - mark ALL workers in this park as verified
      setData(prev => {
        if (!prev?.zones) return prev;
        const newZones = {};
        Object.entries(prev.zones).forEach(([zone, employees]) => {
          newZones[zone] = employees.map(emp => {
            if (emp.park_id === parkId) {
              return { ...emp, verified: true };
            }
            return emp;
          });
        });
        return { ...prev, zones: newZones };
      });
      
      toast.success(`${parkName} verified`);
    } catch (error) {
      toast.error('Failed to verify park');
    } finally {
      setVerifyingRecord(null);
    }
  };

  // Filter data based on selected zone and division
  const filterByZoneAndDivision = (items, zoneKey = 'zone', divisionKey = 'division') => {
    if (!items) return items;
    
    let filtered = items;
    if (selectedZone) {
      filtered = filtered.filter(item => item[zoneKey] === selectedZone);
    }
    if (selectedDivision) {
      filtered = filtered.filter(item => item[divisionKey] === selectedDivision);
    }
    // Sort by park name alphabetically
    return sortParksAlphabetically(filtered);
  };

  // Sort parks alphabetically
  const sortParksAlphabetically = (arr, nameKey = 'park_name') => {
    if (!arr) return [];
    return [...arr].sort((a, b) => (a[nameKey] || '').localeCompare(b[nameKey] || ''));
  };

  // Get filtered zones data - with zones sorted numerically and search
  const getFilteredZonesData = () => {
    if (!data?.zones) return {};
    
    // Helper to sort employees by park name
    const sortByParkName = (employees) => {
      return [...employees].sort((a, b) => {
        const parkA = (a.park_name || '').toLowerCase();
        const parkB = (b.park_name || '').toLowerCase();
        return parkA.localeCompare(parkB);
      });
    };
    
    // Helper to filter by search term
    const filterBySearch = (employees) => {
      if (!searchTerm) return employees;
      const search = searchTerm.toLowerCase();
      return employees.filter(emp => 
        emp.park_name?.toLowerCase().includes(search) ||
        emp.employee_name?.toLowerCase().includes(search) ||
        emp.employee_id?.toLowerCase().includes(search) ||
        emp.inspector_name?.toLowerCase().includes(search) ||
        emp.division?.toLowerCase().includes(search) ||
        emp.category?.toLowerCase().includes(search)
      );
    };
    
    // Helper to filter by verification status (based on PARK, not individual worker)
    // A park is verified if ANY worker in that park is verified
    const filterByVerification = (employees) => {
      if (verificationFilter === 'all') return employees;
      
      // Build park verification map
      const parkVerified = {};
      employees.forEach(emp => {
        if (emp.verified === true) {
          parkVerified[emp.park_id] = true;
        } else if (parkVerified[emp.park_id] === undefined) {
          parkVerified[emp.park_id] = false;
        }
      });
      
      // Filter employees based on their park's verification status
      return employees.filter(emp => {
        const isParkVerified = parkVerified[emp.park_id] === true;
        return verificationFilter === 'verified' ? isParkVerified : !isParkVerified;
      });
    };
    
    if (!selectedZone && !selectedDivision) {
      // Sort zone keys numerically
      const sortedZones = {};
      sortNumeric(Object.keys(data.zones)).forEach(zone => {
        const filtered = filterByVerification(filterBySearch(sortByParkName(data.zones[zone])));
        if (filtered.length > 0) {
          sortedZones[zone] = filtered;
        }
      });
      return sortedZones;
    }
    
    const filteredZones = {};
    sortNumeric(Object.keys(data.zones)).forEach(zone => {
      if (selectedZone && zone !== selectedZone) return;
      
      const employees = data.zones[zone];
      let filteredEmployees = selectedDivision 
        ? employees.filter(emp => emp.division === selectedDivision)
        : employees;
      
      filteredEmployees = filterByVerification(filterBySearch(filteredEmployees));
      
      if (filteredEmployees.length > 0) {
        filteredZones[zone] = sortByParkName(filteredEmployees);
      }
    });
    
    return filteredZones;
  };

  const downloadExcel = () => {
    let rows = [];
    let filename = '';
    
    if (activeTab === 'zone-attendance' && data?.zones) {
      filename = `Zone_Attendance_${date}`;
      rows.push(['Date', 'Time', 'Zone', 'Division', 'Park ID', 'Park Name', 'Employee ID', 'Employee Name', 'Category', 'Status', 'Cleanliness', 'Tool Kit', 'Supervisor Name', 'Remarks']);
      const filteredZones = getFilteredZonesData();
      Object.entries(filteredZones).forEach(([zone, employees]) => {
        employees.forEach(emp => {
          rows.push([
            emp.date || date,
            emp.visit_time || '-',
            zone, 
            emp.division || '-',
            emp.park_id, 
            emp.park_name, 
            emp.employee_id, 
            emp.employee_name,
            emp.category || '-',
            emp.status,
            emp.cleanliness || '-',
            emp.tools_available !== undefined && emp.tools_available !== null ? (emp.tools_available ? 'Yes' : 'No') : '-',
            emp.inspector_name || emp.inspector_id || '-',
            emp.remarks || '-'
          ]);
        });
      });
    } else if (activeTab === 'closed-parks' && data?.closed_parks) {
      filename = `Closed_Parks_${date}`;
      rows.push(['Park ID', 'Park Name', 'Zone', 'Division', 'Supervisor Name', 'Time (IST)', 'Remarks']);
      const filtered = filterByZoneAndDivision(data.closed_parks);
      filtered.forEach(p => {
        rows.push([
          p.park_id, 
          p.park_name, 
          p.zone, 
          p.division || '-', 
          p.inspector_name || p.inspector_id || '-', 
          p.end_time_ist || '-',
          p.remarks || '-'
        ]);
      });
    } else if (activeTab === 'temp-workers' && data?.temp_workers) {
      filename = `Temp_Workers_${date}`;
      rows.push(['Date', 'Park ID', 'Park Name', 'Zone', 'Division', 'Supervisor Name', 'Temp Worker Name', 'Reason']);
      const filtered = filterByZoneAndDivision(data.temp_workers);
      filtered.forEach(tw => {
        rows.push([
          tw.date,
          tw.park_id,
          tw.park_name,
          tw.zone,
          tw.division || '-',
          tw.supervisor_name || tw.supervisor_id || '-',
          tw.temp_worker_name,
          tw.reason || '-'
        ]);
      });
    }
    
    if (rows.length <= 1) {
      toast.error('No data to download');
      return;
    }
    
    // Create CSV content
    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded successfully');
  };

  // Download No Manpower Excel
  const downloadNoManpowerExcel = () => {
    if (!data?.parks) return;
    
    const filteredParks = filterByZoneAndDivision(data.parks);
    const rows = [['S.No', 'Park ID', 'Park Name', 'Zone', 'Division', 'Supervisor', 'Visit Time', 'Cleanliness', 'Remarks']];
    
    filteredParks.forEach((park, idx) => {
      rows.push([
        idx + 1,
        park.park_id || '',
        park.park_name || '',
        park.zone || '',
        park.division || '',
        park.inspector_name || park.inspector_id || '',
        park.visit_time || '',
        park.cleanliness || '',
        park.remarks || ''
      ]);
    });
    
    // Create CSV content
    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `No_Manpower_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded successfully');
  };

  // Handle image download
  const handleImageDownload = (url, filename) => {
    if (!url) return;
    
    // For base64 images
    if (url.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // For external URLs, open in new tab for download
      window.open(url, '_blank');
    }
  };

  // Sort function for numeric strings (lowest to highest)
  const sortNumeric = (arr) => {
    return [...arr].sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      // If both are valid numbers, sort numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      // Otherwise sort alphabetically
      return String(a).localeCompare(String(b));
    });
  };

  // Filter component
  const FilterBar = () => (
    <div className="bg-slate-100 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
      <Filter className="w-5 h-5 text-slate-600" />
      <select
        value={selectedZone}
        onChange={(e) => setSelectedZone(e.target.value)}
        className="h-9 px-3 rounded border border-slate-300 text-sm focus:border-blue-500 focus:ring-0"
        data-testid="zone-filter"
      >
        <option value="">All Zones</option>
        {sortNumeric(zones).map(zone => (
          <option key={zone} value={zone}>{zone}</option>
        ))}
      </select>
      <select
        value={selectedDivision}
        onChange={(e) => setSelectedDivision(e.target.value)}
        className="h-9 px-3 rounded border border-slate-300 text-sm focus:border-blue-500 focus:ring-0"
        disabled={!selectedZone}
        data-testid="division-filter"
      >
        <option value="">All Divisions</option>
        {sortNumeric(divisions).map(div => (
          <option key={div} value={div}>{div}</option>
        ))}
      </select>
      {(selectedZone || selectedDivision) && (
        <button
          onClick={clearFilters}
          className="h-9 px-3 text-sm text-red-600 hover:bg-red-50 rounded border border-red-200"
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:opacity-80">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Reports
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-slate-600" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center gap-2"
              data-testid="refresh-reports-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            {activeTab !== 'no-manpower' && (
              <button
                onClick={downloadExcel}
                className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download Excel
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('zone-attendance')}
            className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
              activeTab === 'zone-attendance'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Zone Attendance
          </button>
          <button
            onClick={() => setActiveTab('closed-parks')}
            className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
              activeTab === 'closed-parks'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Closed Parks
          </button>
          <button
            onClick={() => setActiveTab('no-manpower')}
            className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
              activeTab === 'no-manpower'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            No Manpower
          </button>
          <button
            onClick={() => setActiveTab('temp-workers')}
            className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
              activeTab === 'temp-workers'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Temp Workers
          </button>
          <button
            onClick={() => setActiveTab('employee-master')}
            className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors ${
              activeTab === 'employee-master'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Employee Master
          </button>
        </div>

        {/* Zone & Division Filters */}
        <FilterBar />

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {activeTab === 'zone-attendance' && data && (
              <div className="space-y-4">
                {/* Summary Cards - Single Line Compact */}
                {(() => {
                  const counts = getVerificationCounts();
                  return (
                    <div className="flex items-center gap-6 mb-2 py-2 px-3 bg-slate-50 rounded-lg text-sm">
                      <span className="font-bold text-slate-700">Total: <span className="text-lg">{counts.total}</span> parks</span>
                      <span className="text-slate-300">|</span>
                      <span className="font-bold text-green-700">Verified: <span className="text-lg">{counts.verified}</span></span>
                      <span className="text-slate-300">|</span>
                      <span className="font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">Pending: <span className="text-lg">{counts.pending}</span></span>
                    </div>
                  );
                })()}

                {/* Search Bar and Controls - Compact */}
                <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-2 rounded-lg mb-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search park, employee..."
                    className="flex-1 min-w-[150px] h-9 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 text-sm"
                    data-testid="zone-attendance-search"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="h-9 px-2 bg-slate-200 hover:bg-slate-300 rounded-lg font-semibold text-xs"
                    >
                      Clear
                    </button>
                  )}
                  {/* Verification Filter Dropdown */}
                  <select
                    value={verificationFilter}
                    onChange={(e) => setVerificationFilter(e.target.value)}
                    className="h-9 px-2 rounded-lg border-2 border-slate-300 text-xs font-semibold bg-white"
                    data-testid="verification-filter"
                  >
                    {(() => {
                      const counts = getVerificationCounts();
                      return (
                        <>
                          <option value="all">All ({counts.total})</option>
                          <option value="verified">Verified ({counts.verified})</option>
                          <option value="pending">Pending ({counts.pending})</option>
                        </>
                      );
                    })()}
                  </select>
                  <button
                    onClick={collapseAllZones}
                    className="h-8 px-2 bg-slate-200 hover:bg-slate-300 rounded text-xs font-semibold"
                  >
                    Collapse
                  </button>
                  <button
                    onClick={expandAllZones}
                    className="h-8 px-2 bg-slate-200 hover:bg-slate-300 rounded text-xs font-semibold"
                  >
                    Expand
                  </button>
                </div>
                
                {Object.keys(getFilteredZonesData()).length === 0 ? (
                  <p className="text-center text-slate-600 py-8">No attendance data for this date</p>
                ) : (
                  <div className="overflow-x-auto overflow-y-auto max-h-[72vh] relative border rounded-lg">
                    <table className="w-full text-xs min-w-[1800px] border-collapse">
                      <thead className="sticky top-0 z-20">
                        <tr className="bg-slate-100">
                          <th className="text-center p-2 font-bold bg-green-100 min-w-[90px] sticky left-0 z-30 border-r border-green-200">Verify</th>
                          <th className="text-left p-2 font-bold sticky left-[90px] z-30 bg-slate-100 min-w-[60px] border-r border-slate-200">Zone</th>
                          <th className="text-left p-2 font-bold sticky left-[150px] z-30 bg-slate-100 min-w-[200px] border-r border-slate-200">Park</th>
                          <th className="text-left p-2 font-bold sticky left-[350px] z-30 bg-slate-100 min-w-[70px] border-r-2 border-slate-300">Division</th>
                          <th className="text-left p-2 font-bold">Date</th>
                          <th className="text-left p-2 font-bold">Time</th>
                          <th className="text-left p-2 font-bold">Employee</th>
                          <th className="text-left p-2 font-bold">Category</th>
                          <th className="text-left p-2 font-bold">Supervisor</th>
                          <th className="text-center p-2 font-bold">Status</th>
                          <th className="text-center p-2 font-bold bg-yellow-50">Cleanliness</th>
                          <th className="text-center p-2 font-bold bg-purple-50">Tool Kit</th>
                          <th className="text-left p-2 font-bold">Remarks</th>
                          <th className="text-center p-2 font-bold">Park Front</th>
                          <th className="text-center p-2 font-bold">Workers 1</th>
                          <th className="text-center p-2 font-bold">Workers 2</th>
                          <th className="text-center p-2 font-bold">Workers 3</th>
                          <th className="text-center p-2 font-bold">Clean 1</th>
                          <th className="text-center p-2 font-bold">Clean 2</th>
                          <th className="text-center p-2 font-bold">Clean 3</th>
                          <th className="text-center p-2 font-bold bg-blue-50">Correction Photo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(getFilteredZonesData()).map(([zone, employees]) => {
                          const zoneParkCounts = getZoneParkCounts(zone);
                          // Track which parks we've already shown verify button for
                          const shownParks = new Set();
                          return (
                          <React.Fragment key={zone}>
                            {/* Collapsible Zone Header Row */}
                            <tr 
                              className="bg-blue-100 hover:bg-blue-200 cursor-pointer border-t-2 border-blue-300"
                              onClick={() => toggleZone(zone)}
                            >
                              <td colSpan={20} className="p-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {collapsedZones[zone] ? (
                                      <ChevronRight className="w-5 h-5 text-blue-700" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-blue-700" />
                                    )}
                                    <span className="font-bold text-blue-800 text-base">Zone {zone}</span>
                                    <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-sm font-semibold">{zoneParkCounts.total} parks</span>
                                    <span className="bg-green-200 text-green-700 px-2 py-0.5 rounded text-sm font-semibold">{zoneParkCounts.verified} verified</span>
                                    <span className="bg-orange-200 text-orange-700 px-2 py-0.5 rounded text-sm font-semibold">{zoneParkCounts.pending} pending</span>
                                  </div>
                                  <span className="text-sm text-blue-600">
                                    {collapsedZones[zone] ? 'Click to expand' : 'Click to collapse'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {/* Zone Data Rows */}
                            {!collapsedZones[zone] && employees.map((record, idx) => {
                              const isFirstRowForPark = !shownParks.has(record.park_id);
                              if (isFirstRowForPark) shownParks.add(record.park_id);
                              const isVerifying = verifyingRecord === record.park_id;
                              // Check if park is verified from ORIGINAL data
                              const originalZoneData = data?.zones?.[zone] || [];
                              const isParkVerified = originalZoneData.some(e => e.park_id === record.park_id && e.verified === true);
                              return (
                            <tr key={`${zone}-${idx}`} className={`border-t hover:bg-slate-50 ${record.is_temporary ? 'bg-red-100 hover:bg-red-200' : ''} ${record.corrected_by_supervisor ? 'bg-blue-50 hover:bg-blue-100' : ''} ${record.manually_edited && !record.corrected_by_supervisor ? 'bg-red-50 hover:bg-red-100' : ''}`}>
                              {/* Verify Button Cell - STICKY, only show button on first row of each park */}
                              <td className={`p-2 text-center sticky left-0 z-10 border-r border-green-200 ${isParkVerified ? 'bg-green-100' : 'bg-green-50'}`}>
                                {isFirstRowForPark ? (
                                  isParkVerified ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-200 text-green-700 rounded text-xs font-bold">
                                      <CheckCircle className="w-4 h-4" />
                                      Verified
                                    </span>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleVerifyPark(record.park_id, record.park_name); }}
                                      disabled={isVerifying}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-bold disabled:opacity-50"
                                    >
                                      {isVerifying ? (
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <Circle className="w-3 h-3" />
                                      )}
                                      Verify
                                    </button>
                                  )
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className={`p-2 font-semibold text-blue-600 sticky left-[90px] z-10 border-r border-slate-200 ${record.is_temporary ? 'bg-red-100' : 'bg-white'}`}>{zone}</td>
                              <td className={`p-2 sticky left-[150px] z-10 border-r border-slate-200 ${record.is_temporary ? 'bg-red-100' : 'bg-white'}`}>
                                <div 
                                  className="min-w-[200px] text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" 
                                  title={`Click to view attendance calendar for ${record.park_name}`}
                                  onClick={() => navigate(`/admin/calendar?park_id=${record.park_id}`)}
                                >
                                  {record.park_name}
                                </div>
                              </td>
                              <td className={`p-2 sticky left-[350px] z-10 border-r-2 border-slate-300 ${record.is_temporary ? 'bg-red-100' : 'bg-white'}`}>{record.division || '-'}</td>
                              <td className="p-2">{record.date || '-'}</td>
                              <td className="p-2">{record.visit_time || '-'}</td>
                              <td className={`p-2 ${record.is_temporary ? 'text-red-700 font-bold' : ''}`}>
                                <div className="flex items-center gap-1">
                                  {record.is_temporary && <span className="w-2 h-2 bg-red-600 rounded-full flex-shrink-0" title="Temporary Worker"></span>}
                                  <span className={record.is_temporary ? 'text-red-700' : ''}>{record.employee_name}</span>
                                  {record.is_temporary && <span className="text-xs bg-red-600 text-white px-1 rounded ml-1">TEMP</span>}
                                </div>
                              </td>
                              <td className={`p-2 ${record.is_temporary ? 'text-red-600 font-semibold' : ''}`}>{record.is_temporary ? 'Temporary' : record.category || '-'}</td>
                              <td className="p-2">
                                {record.is_ot_visit ? (
                                  <div className="flex items-center gap-1">
                                    <span>{record.inspector_name || record.inspector_id || '-'}</span>
                                    <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">OT</span>
                                  </div>
                                ) : (
                                  record.inspector_name || record.inspector_id || '-'
                                )}
                              </td>
                              <td className="p-2 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                                      record.status === 'Present'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {record.status}
                                  </span>
                                  {/* Supervisor Correction Indicator */}
                                  {record.corrected_by_supervisor && (
                                    <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded flex items-center gap-1">
                                      <Edit className="w-2.5 h-2.5" />
                                      SUP CORRECTED
                                    </span>
                                  )}
                                  {/* Admin Edited Indicator */}
                                  {record.manually_edited && !record.corrected_by_supervisor && (
                                    <span className="px-2 py-0.5 bg-red-700 text-white text-[10px] font-bold rounded flex items-center gap-1">
                                      <Edit className="w-2.5 h-2.5" />
                                      ADMIN EDIT
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* Cleanliness Status */}
                              <td className="p-2 text-center bg-yellow-50">
                                {record.cleanliness ? (
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-bold ${
                                      record.cleanliness === 'Good'
                                        ? 'bg-green-100 text-green-700'
                                        : record.cleanliness === 'Average'
                                        ? 'bg-orange-100 text-orange-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {record.cleanliness}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Tool Kit Availability */}
                              <td className="p-2 text-center bg-purple-50">
                                {record.tools_available !== undefined && record.tools_available !== null ? (
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-bold ${
                                      record.tools_available
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {record.tools_available ? 'Yes' : 'No'}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Remarks Column */}
                              <td className="p-2 text-left max-w-[200px]">
                                <span className="text-xs text-slate-600 truncate block" title={record.remarks || '-'}>
                                  {record.remarks || '-'}
                                </span>
                              </td>
                              {/* Park Front Photo (Selfie) */}
                              <td className="p-2 text-center">
                                {record.selfie_photo_url ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.selfie_photo_url, 
                                      title: `${record.park_name} - Park Front Photo` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Workers Photo 1 (main) */}
                              <td className="p-2 text-center">
                                {record.workers_photo_url ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.workers_photo_url, 
                                      title: `${record.park_name} - Workers Photo 1` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Workers Photo 2 (additional_1) */}
                              <td className="p-2 text-center">
                                {record.additional_photos?.[0] ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.additional_photos[0], 
                                      title: `${record.park_name} - Workers Photo 2` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Workers Photo 3 (additional_2) */}
                              <td className="p-2 text-center">
                                {record.additional_photos?.[1] ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.additional_photos[1], 
                                      title: `${record.park_name} - Workers Photo 3` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Cleanliness Photo 1 */}
                              <td className="p-2 text-center">
                                {(record.cleanliness_photos?.[0] || record.cleanliness_photo_url) ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.cleanliness_photos?.[0] || record.cleanliness_photo_url, 
                                      title: `${record.park_name} - Cleanliness Photo 1` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Cleanliness Photo 2 */}
                              <td className="p-2 text-center">
                                {record.cleanliness_photos?.[1] ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.cleanliness_photos[1], 
                                      title: `${record.park_name} - Cleanliness Photo 2` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Cleanliness Photo 3 */}
                              <td className="p-2 text-center">
                                {record.cleanliness_photos?.[2] ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.cleanliness_photos[2], 
                                      title: `${record.park_name} - Cleanliness Photo 3` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {/* Correction Photo (from supervisor correction) */}
                              <td className="p-2 text-center bg-blue-50">
                                {record.correction_photo_url ? (
                                  <button
                                    onClick={() => setImageModal({ 
                                      url: record.correction_photo_url, 
                                      title: `${record.employee_name} - Supervisor Correction Photo` 
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-semibold"
                                  >
                                    <Image className="w-3 h-3" />
                                    View
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                          })}
                          </React.Fragment>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'closed-parks' && data && (
              <div className="space-y-4">
                {filterByZoneAndDivision(data.closed_parks || []).length === 0 ? (
                  <p className="text-center text-slate-600 py-8">No closed parks for this date</p>
                ) : (
                  filterByZoneAndDivision(data.closed_parks).map((park, idx) => (
                    <div key={idx} className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                      <h3 className="font-bold text-lg mb-2">{park.park_name}</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-semibold">Zone:</span> {park.zone}
                        </div>
                        <div>
                          <span className="font-semibold">Division:</span> {park.division || '-'}
                        </div>
                        <div>
                          <span className="font-semibold">Supervisor:</span> {park.inspector_name || park.inspector_id}
                        </div>
                        <div>
                          <span className="font-semibold">Time:</span> {park.end_time_ist || '-'}
                        </div>
                        <div className="col-span-2">
                          <span className="font-semibold">Remarks:</span> {park.remarks || 'N/A'}
                        </div>
                      </div>
                      {park.closed_photo_url && (
                        <div className="mt-3">
                          <button
                            onClick={() => setImageModal({ 
                              url: park.closed_photo_url, 
                              title: `${park.park_name} - Closed Photo` 
                            })}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded font-semibold text-sm"
                          >
                            <Image className="w-4 h-4" />
                            View Photo
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Temp Workers Tab */}
            {activeTab === 'temp-workers' && data && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Temporary Workers Report - {data.date}</h3>
                    <p className="text-sm text-slate-500">
                      Total: {filterByZoneAndDivision(data.temp_workers || []).length} temporary workers
                      {(selectedZone || selectedDivision) && ` (filtered)`}
                    </p>
                  </div>
                </div>
                {filterByZoneAndDivision(data.temp_workers || []).length === 0 ? (
                  <p className="text-center text-slate-600 py-8">No temporary workers used on this date</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-3 font-bold">Park</th>
                          <th className="text-left p-3 font-bold">Zone / Division</th>
                          <th className="text-left p-3 font-bold">Supervisor</th>
                          <th className="text-left p-3 font-bold">Temp Worker Name</th>
                          <th className="text-left p-3 font-bold">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterByZoneAndDivision(data.temp_workers).map((tw, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-3">
                              <div className="font-semibold">{tw.park_name}</div>
                              <div className="text-xs text-slate-500">{tw.park_id}</div>
                            </td>
                            <td className="p-3">{tw.zone} {tw.division && `/ ${tw.division}`}</td>
                            <td className="p-3">{tw.supervisor_name || tw.supervisor_id}</td>
                            <td className="p-3 font-semibold">{tw.temp_worker_name}</td>
                            <td className="p-3">{tw.reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Employee Master Tab */}
            {activeTab === 'employee-master' && (
              <div>
                {/* Summary Dashboard - Always shows full counts */}
                {(employeeMasterSummary || employeeMasterData?.summary) && (
                  <div className="mb-6">
                    <h3 className="font-bold text-lg mb-4">Employee Category Dashboard</h3>
                    <div className="flex flex-wrap gap-3 mb-6">
                      {(employeeMasterSummary || employeeMasterData.summary).category_breakdown.map((cat) => (
                        <div 
                          key={cat.category}
                          onClick={() => setEmployeeCategoryFilter(
                            employeeCategoryFilter.toLowerCase() === cat.category.toLowerCase() ? 'all' : cat.category.toLowerCase()
                          )}
                          className={`p-3 rounded-lg cursor-pointer transition-all min-w-[100px] ${
                            employeeCategoryFilter.toLowerCase() === cat.category.toLowerCase()
                              ? 'bg-blue-600 text-white shadow-lg scale-105'
                              : 'bg-white border-2 border-slate-200 hover:border-blue-400'
                          }`}
                        >
                          <div className="text-xl font-bold">{cat.count}</div>
                          <div className="text-xs font-semibold">{cat.category}</div>
                        </div>
                      ))}
                      <div 
                        onClick={() => setEmployeeCategoryFilter('all')}
                        className={`p-3 rounded-lg cursor-pointer transition-all min-w-[100px] ${
                          employeeCategoryFilter === 'all'
                            ? 'bg-green-600 text-white shadow-lg scale-105'
                            : 'bg-white border-2 border-slate-200 hover:border-green-400'
                        }`}
                      >
                        <div className="text-xl font-bold">{(employeeMasterSummary || employeeMasterData.summary).total_employees}</div>
                        <div className="text-xs font-semibold">Total</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filters and Download */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedZone}
                      onChange={(e) => setSelectedZone(e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="">All Zones</option>
                      {zones.map(z => (
                        <option key={z} value={z}>Zone {z}</option>
                      ))}
                    </select>
                    <select
                      value={employeeCategoryFilter}
                      onChange={(e) => setEmployeeCategoryFilter(e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="all">All Categories</option>
                      {(employeeMasterSummary || employeeMasterData?.summary)?.category_breakdown?.map(cat => (
                        <option key={cat.category} value={cat.category.toLowerCase()}>{cat.category}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Search employee..."
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      className="px-3 py-2 border rounded-lg w-48"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const response = await adminAPI.downloadEmployeeMaster(selectedZone || 'all', employeeCategoryFilter);
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `employee_master_${selectedZone || 'all'}_${employeeCategoryFilter}.xlsx`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('Downloaded successfully');
                      } catch (error) {
                        toast.error('Download failed');
                      }
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel
                  </button>
                </div>

                {/* Employee List */}
                {employeeMasterData?.employees && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">
                      Showing {employeeMasterData.employees.filter(emp => {
                        const matchCategory = employeeCategoryFilter === 'all' || emp.category.toLowerCase() === employeeCategoryFilter.toLowerCase();
                        const matchSearch = !employeeSearchTerm || 
                          emp.employee_name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                          emp.employee_id.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                          emp.park_name.toLowerCase().includes(employeeSearchTerm.toLowerCase());
                        return matchCategory && matchSearch;
                      }).length} employees
                    </p>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-100">
                          <tr>
                            <th className="text-left p-3 font-bold">S.No</th>
                            <th className="text-left p-3 font-bold">Employee ID</th>
                            <th className="text-left p-3 font-bold">Employee Name</th>
                            <th className="text-left p-3 font-bold">Category</th>
                            <th className="text-left p-3 font-bold">Park Name</th>
                            <th className="text-left p-3 font-bold">Zone</th>
                            <th className="text-left p-3 font-bold">Division</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeMasterData.employees
                            .filter(emp => {
                              const matchCategory = employeeCategoryFilter === 'all' || emp.category.toLowerCase() === employeeCategoryFilter.toLowerCase();
                              const matchSearch = !employeeSearchTerm || 
                                emp.employee_name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                emp.employee_id.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                emp.park_name.toLowerCase().includes(employeeSearchTerm.toLowerCase());
                              return matchCategory && matchSearch;
                            })
                            .map((emp, idx) => (
                              <tr key={emp.employee_id + idx} className="border-t hover:bg-slate-50">
                                <td className="p-3">{idx + 1}</td>
                                <td className="p-3 font-mono text-xs">{emp.employee_id}</td>
                                <td className="p-3 font-semibold">{emp.employee_name}</td>
                                <td className="p-3">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    emp.category === 'Sweeper' ? 'bg-orange-100 text-orange-700' :
                                    emp.category === 'Gardener' ? 'bg-green-100 text-green-700' :
                                    emp.category === 'Security' ? 'bg-blue-100 text-blue-700' :
                                    emp.category === 'Electrician' ? 'bg-yellow-100 text-yellow-700' :
                                    emp.category === 'Supervisor' ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {emp.category}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div>{emp.park_name}</div>
                                  <div className="text-xs text-slate-500">{emp.park_id}</div>
                                </td>
                                <td className="p-3">{emp.zone || '-'}</td>
                                <td className="p-3">{emp.division || '-'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!employeeMasterData && !loading && (
                  <p className="text-center text-slate-600 py-8">Click refresh to load employee master data</p>
                )}
              </div>
            )}

            {/* No Manpower Tab */}
            {activeTab === 'no-manpower' && data && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">No Manpower Present - {data.date}</h3>
                    <p className="text-sm text-slate-500">
                      Parks where no workers were present: {filterByZoneAndDivision(data.parks || []).length}
                      {(selectedZone || selectedDivision) && ` (filtered)`}
                    </p>
                  </div>
                  <button
                    onClick={() => downloadNoManpowerExcel()}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel
                  </button>
                </div>
                {filterByZoneAndDivision(data.parks || []).length === 0 ? (
                  <p className="text-center text-slate-600 py-8">All open parks had workers present on this date</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-3 font-bold">S.No</th>
                          <th className="text-left p-3 font-bold">Park Name</th>
                          <th className="text-left p-3 font-bold">Zone</th>
                          <th className="text-left p-3 font-bold">Division</th>
                          <th className="text-left p-3 font-bold">Supervisor</th>
                          <th className="text-left p-3 font-bold">Visit Time</th>
                          <th className="text-left p-3 font-bold">Cleanliness</th>
                          <th className="text-left p-3 font-bold">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterByZoneAndDivision(data.parks).map((park, idx) => (
                          <tr key={park.park_id + idx} className="border-t hover:bg-slate-50">
                            <td className="p-3">{idx + 1}</td>
                            <td className="p-3">
                              <div className="font-semibold">{park.park_name}</div>
                              <div className="text-xs text-slate-500">{park.park_id}</div>
                            </td>
                            <td className="p-3">{park.zone || '-'}</td>
                            <td className="p-3">{park.division || '-'}</td>
                            <td className="p-3">{park.inspector_name || park.inspector_id || '-'}</td>
                            <td className="p-3">{park.visit_time || '-'}</td>
                            <td className="p-3">{park.cleanliness || '-'}</td>
                            <td className="p-3">{park.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {imageModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setImageModal(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg">{imageModal.title}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleImageDownload(imageModal.url, `${imageModal.title}.jpg`)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button 
                  onClick={() => setImageModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              <img
                src={imageModal.url}
                alt={imageModal.title}
                className="max-w-full h-auto mx-auto rounded-lg"
                style={{ maxHeight: '70vh' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
